import { last } from "@punks/core"
import React from "react"
import {
  CalendarEventAvailabilityPage,
  CalendarEventAvailabilitySlot,
  CalendarEventDataFragment,
} from "../../../../../api/backend/backend-api"
import CustomCalendar from "../../../../../ui/organisms/CustomCalendar"
import { CalendarDate } from "../../../../../ui/organisms/CustomCalendar/types"
import {
  addCalendarMonths,
  todayCalendarDate,
} from "../../../../../ui/organisms/CustomCalendar/utils"
import { EditorialLabel } from "../../../../contents"
import EventCalendarDayHours from "../EventCalendarDayHours"

interface Props {
  calendar: CalendarEventDataFragment
  selectedDate?: CalendarDate
  selectedSlot?: CalendarEventAvailabilitySlot
  onSelectedDateChange: (value?: CalendarDate) => void
  onSelectedSlotChange: (value: CalendarEventAvailabilitySlot) => void
  onSlotConfirm: (value: CalendarEventAvailabilitySlot) => void
}

const hasAvailableSlots = (page: CalendarEventAvailabilityPage) =>
  page.days.some((x) => x.slots.length > 0)

const getMinDate = (calendar: CalendarEventDataFragment) =>
  calendar.availability.pages.filter((x) => hasAvailableSlots(x))?.[0]
    ?.days[0] ?? todayCalendarDate()

const getMaxDate = (calendar: CalendarEventDataFragment) => {
  const lastPage = last(calendar.availability.pages)
  return lastPage
    ? last(lastPage.days)
    : addCalendarMonths(todayCalendarDate(), 1)
}

const getMonthPage = (
  calendar: CalendarEventDataFragment,
  date: CalendarDate
) =>
  calendar.availability.pages.find(
    (x) => x.year === date.year && x.month === date.month
  )

const getCalendarDayItem = (
  page: CalendarEventAvailabilityPage,
  date: CalendarDate
) =>
  page.days.find(
    (x) => x.day === date.day && x.month === date.month && x.year === date.year
  )

const EventCalendarSelect = ({
  calendar,
  selectedDate,
  selectedSlot,
  onSelectedDateChange,
  onSelectedSlotChange,
  onSlotConfirm,
}: Props) => {
  const [currentDate, setCurrentDate] = React.useState<CalendarDate>(
    getMinDate(calendar)
  )

  const currentMonthPage = getMonthPage(calendar, currentDate)
  const selectedDayAvailability = selectedDate
    ? getCalendarDayItem(
        getMonthPage(calendar, selectedDate) as any,
        selectedDate
      )
    : undefined

  const isSelectable = (date: CalendarDate) => {
    return !!(
      currentMonthPage &&
      (getCalendarDayItem(currentMonthPage, date)?.slots?.length ?? 0) > 0
    )
  }

  return (
    <div>
      <div className="mb-4">
        <EditorialLabel
          scope="calendar"
          id="select-title"
          variant="subtitle2"
        />
      </div>
      <div className="flex md:mt-10">
        {calendar.definition.options?.preSelection?.fixed !== true && (
          <div className={`w-full ${selectedDate ? "hidden md:block" : ""}`}>
            <CustomCalendar
              min={getMinDate(calendar)}
              max={getMaxDate(calendar)}
              today={todayCalendarDate()}
              currentPageDate={currentDate}
              onPageDateChange={setCurrentDate}
              enableMonthControls
              selectable={isSelectable}
              onSelect={onSelectedDateChange}
              selected={selectedDate}
            />
          </div>
        )}
        {selectedDayAvailability && (
          <EventCalendarDayHours
            day={selectedDayAvailability}
            selected={selectedSlot}
            onSelectedChanged={onSelectedSlotChange}
            onConfirm={onSlotConfirm}
            onBack={() => onSelectedDateChange(undefined)}
            backHidden={
              calendar.definition.options?.preSelection?.fixed ?? false
            }
          />
        )}
      </div>
    </div>
  )
}

export default EventCalendarSelect
